<script setup lang="ts">
  import "vue-i18n";
  import "core-js/modules/es.array.at";
  import "core-js/modules/es.string.at-alternative";

  import { ref, onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { RouterView } from "vue-router";
  import { Theme, NotificationContainer, useStatusBar, useNotification, useLanguage } from "@ui-kit";
  import {
    useDeviceInfo,
    useBarcode,
    useBattery,
    useKeyEvent,
    useReadRfid,
    usePowerRfid,
    useDeviceInfo_v2,
    useUserAgent,
  } from "@plugins";
  import { useShopLoad } from "@src/api";
  import { useProfileOptions } from "@src/hooks";
  import { useUserInfoStore } from "@src/store";
  import { Logging } from "@src/components";

  import "../modules/shopflow_uikit/src/styles/variables.css";
  import "../modules/shopflow_uikit/src/styles/global.css";
  import "../modules/shopflow_uikit/src/styles/fonts.css";

  const device_info = useDeviceInfo_v2();
  const user_agent = useUserAgent();

  const language = useLanguage();
  const battery = useBattery();
  const barcode = useBarcode();
  const deviceKeyEvent = useKeyEvent();
  const readRfid = useReadRfid();
  const powerRfid = usePowerRfid();
  const userInfoStore = useUserInfoStore();
  const { getInfo } = useDeviceInfo();
  const {
    setBatteryLevel,
    initRfidStatus,
    initBarcodeStatus,
    initServerStatus,
    setBarcodeStatus,
    setRfidStatus,
    setRfidPower,
  } = useStatusBar();
  const { getProfile } = useProfileOptions();
  const { t } = useI18n();
  const { execute: shopLoad } = useShopLoad();
  const { show } = useNotification();

  const isLoading = ref(true);

  const setUserAgent = async () => {
    const apk_version = device_info.state.apk_version || "";
    // @ts-expect-error версия ui из env
    const ui_version = import.meta.env.VITE_APP_VERSION;

    await user_agent.appendOrReplace("ShopflowGo", `${apk_version}/${ui_version}`);
  };

  onMounted(async () => {
    await device_info.init();
    await setUserAgent();
    // Получаем информацию об устройстве
    const info = await getInfo();

    // Подгружаем профиль пользователя
    const resultProfile = await getProfile();

    if (resultProfile.isSuccess.value) {
      initServerStatus("neutral");
    }

    // Подгружаем магазин пользователя, если он не был загружен ранее
    if (!userInfoStore.shop && resultProfile.data.value?.current_user.shop_id) {
      const { isSuccess, data } = await shopLoad({ shop_id: resultProfile.data.value.current_user.shop_id });

      if (isSuccess) {
        userInfoStore.setShop(data.value.shop);
      }
    }

    if (info) {
      initRfidStatus(info.isRfidReader ? "neutral" : "error");
      initBarcodeStatus(info.isBarcodeScanner ? "neutral" : "error");
      setBatteryLevel(info.batteryLevel.toString());
    } else {
      show({ title: t("Ошибка при получении информации об устройстве"), type: "error" });
    }

    battery.start();
    battery.subscribe((batteryLevel) => {
      setBatteryLevel(Math.trunc(batteryLevel).toString());
    });

    if (info && info.isBarcodeScanner) {
      barcode.start();
      barcode.subscribe((value) => {
        setBarcodeStatus(value ? "success" : "error");
      });
    }

    if (info && info.isRfidReader) {
      deviceKeyEvent.setEventKeys([info.rfidGunKeyCode || 293]);
      deviceKeyEvent.start();
      readRfid.addKeyEventListener();
      readRfid.subscribe(({ value }) => {
        setRfidStatus(value ? "success" : "error");
      });

      // устанавливаем значение мощности в статус бар и подписываемся на изменения
      setRfidPower(await powerRfid.get());
      powerRfid.subscribe((power) => {
        setRfidPower(power);
      });

      if (resultProfile.data.value?.constants.rfid?.power_write) {
        powerRfid.setPowerWrite(resultProfile.data.value.constants.rfid.power_write);
      }

      if (resultProfile.data.value?.constants.rfid?.power_once_read) {
        powerRfid.set(resultProfile.data.value.constants.rfid.power_once_read);
      }
    }

    // Инициализация языка
    language.init();

    isLoading.value = false;
  });

  onUnmounted(() => {
    battery.stop();
    barcode.stop();
    deviceKeyEvent.stop();
    readRfid.removeKeyEventListener();
  });
</script>

<template>
  <template v-if="isLoading" />
  <template v-else>
    <Theme />
    <NotificationContainer :is-mobile="true" />
    <main class="sf__main">
      <RouterView />
    </main>
    <Logging />
  </template>
</template>

<style>
  * {
    padding: 0px;
    margin: 0px;
    border: none;
  }

  body {
    height: 100vh;
  }

  #shopflow_go_ui {
    height: 100%;
    overflow: hidden;
  }

  .sf__main {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
</style>
